import React from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';
import { FileText, AlertCircle } from 'lucide-react';

interface BusinessJustificationSectionProps {
  justification: string;
  setJustification: (v: string) => void;
  error?: string;
}

export const BusinessJustificationSection: React.FC<BusinessJustificationSectionProps> = ({
  justification,
  setJustification,
  error,
}) => {
  const charCount = justification.length;
  const isTooLong = charCount > 1000;

  return (
    <Card className="border border-border/80 shadow-xs rounded-2xl overflow-hidden font-heading">
      <CardHeader className="pb-3.5 border-b border-border/60 bg-muted/20 flex flex-col sm:flex-row sm:items-center justify-between gap-2">
        <CardTitle className="text-base font-extrabold flex items-center gap-2.5 text-foreground">
          <div className="h-8 w-8 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center text-primary">
            <FileText className="h-4 w-4" />
          </div>
          <div>
            <span>Business Justification</span>
            <p className="text-xs font-semibold text-foreground-secondary mt-0.5">
              Explain operational need and business rationale for audit compliance
            </p>
          </div>
        </CardTitle>
        <span
          className={`text-[11px] font-mono font-bold ${
            isTooLong ? 'text-destructive' : 'text-foreground-muted'
          }`}
        >
          {charCount} / 1000 chars
        </span>
      </CardHeader>
      <CardContent className="pt-5 pb-6 px-6 space-y-2">
        <label className="text-sm font-bold text-foreground flex items-center gap-1">
          <span>Operational or Strategic Rationale</span>
          <span className="text-destructive">*</span>
        </label>
        <textarea
          className={`w-full px-3.5 py-3 text-xs font-medium rounded-xl border bg-surface text-foreground placeholder:text-foreground-muted focus:outline-none focus:ring-2 focus:ring-primary/30 min-h-[90px] transition-all leading-relaxed ${
            error || isTooLong ? 'border-destructive focus:ring-destructive/30' : 'border-border'
          }`}
          placeholder="Briefly state why this procurement is needed, the project or department supported, and any key timing requirements..."
          value={justification}
          onChange={(e) => setJustification(e.target.value)}
        />
        {error && (
          <p className="text-xs font-semibold text-destructive flex items-center gap-1 mt-1">
            <AlertCircle className="h-3.5 w-3.5" /> {error}
          </p>
        )}
      </CardContent>
    </Card>
  );
};
